import { css, keyframes, SerializedStyles } from '@emotion/react';
import { theme } from 'global';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const popIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const popOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  to {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
`;

export const root = css`
  box-sizing: border-box;
  position: relative;
  width: 488px;
  max-width: 100%;
  margin: 0 auto;
  background-color: ${theme.colors.neutral[0]};
  border-radius: 8px;
  padding: 16px;
`;

export const content = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const title = css`
  color: ${theme.colors.secondary};
  margin: 0 0 14px;
`;

export const requirementsList = css`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const requirementItem = css`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: ${theme.colors.neutral[500]};
`;

export const requirementIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  min-width: 20px;
  min-height: 20px;
  margin-top: 1px;
`;

export const dot = css`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background-color: ${theme.colors.neutral[300]};
`;

export const requirementText = css`
  margin: 0;
  color: ${theme.colors.neutral[500]};
`;

// Custom one-off colour specifically used for this component
const customDropzoneHoverColour = '#F9F6FF';

export const dropzone = (
  isDragging: boolean,
  disabled: boolean
): SerializedStyles => css`
  box-sizing: border-box;
  width: 100%;
  min-height: 182px;
  padding: 24px;
  border: 1px dashed ${theme.colors.primary.medium};
  border-radius: 12px;
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  opacity: ${disabled ? 0.6 : 1};
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background-color: ${disabled
      ? theme.colors.white
      : customDropzoneHoverColour};
  }

  ${isDragging &&
  css`
    background-color: ${theme.colors.primary.lightest};
    border-color: ${theme.colors.primary.dark};
    transform: scale(0.99);
  `}

  @media screen and (max-width: ${theme.breaks.xs}px) {
    display: none;
  }
`;

export const hiddenInput = css`
  display: none;
`;

export const uploadIcon = css`
  width: 72px;
  height: 72px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.primary.lightest};
  flex-shrink: 0;
`;

export const browseText = css`
  && {
    margin: 0;
    color: ${theme.colors.primary.medium};
    text-align: center;
    font-weight: 500;
  }
`;

export const dropText = css`
  && {
    margin: 0;
    color: ${theme.colors.neutral[600]};
    text-align: center;
  }
`;

export const dropzoneTextGroup = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
`;

export const mobileUploadButton = (disabled: boolean): SerializedStyles => css`
  display: none;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    appearance: none;
    width: 100%;
    min-height: 50px;
    padding: 7px 12px;
    border: 1px solid ${theme.colors.primary.medium};
    border-radius: 8px;
    background-color: ${theme.colors.white};
    color: ${theme.colors.primary.medium};
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: ${disabled ? 0.6 : 1};
  }
`;

export const mobileUploadText = css`
  && {
    margin: 0;
    color: ${theme.colors.primary.medium};
    text-align: center;
    font-weight: 500;
  }
`;

export const divider = css`
  border: 0;
  border-top: 1px solid ${theme.colors.neutral[200]};
  margin: 0;
  width: 100%;
`;

export const fileList = css`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const fileRow = (isLoaded: boolean): SerializedStyles => css`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 45px;
  border-radius: 7px;
  background-color: ${theme.colors.white};
  padding: 12px;

  ${isLoaded &&
  css`
    background-color: ${theme.colors.primary.lightest};
  `}
`;

export const fileProgress = (progress: number): SerializedStyles => css`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${progress}%;
  background-color: ${theme.colors.primary.lightest};
  border-radius: 7px 0 0 7px;
  transition: width 1s linear;
  opacity: 1;

  @media screen and (max-width: ${theme.breaks.xs}px) {
    border-radius: 5px 0 0 5px;
  }
`;

export const fileContent = css`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const fileName = css`
  margin: 0;
  color: ${theme.colors.neutral[600]};
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const iconButton = (danger = false): SerializedStyles => css`
  appearance: none;
  background: transparent;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: ${danger ? theme.colors.danger : theme.colors.primary.medium};
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const dialogDeleteButton = css`
  background-color: ${theme.colors.danger};
  color: ${theme.colors.white};

  &:hover {
    background-color: ${theme.colors.danger};
  }
`;

export const deleteModalOverlay = (isClosing: boolean): SerializedStyles => css`
  position: fixed;
  inset: 0;
  background-color: rgba(12, 0, 51, 0.18);
  z-index: 1400;
  animation: ${isClosing ? fadeOut : fadeIn} 180ms ease-out forwards;
`;

export const deleteModalPositioner = css`
  position: fixed;
  transform: translate(-50%, -50%);
`;

export const deleteModal = (isClosing: boolean): SerializedStyles => css`
  background-color: ${theme.colors.white};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(12, 0, 51, 0.18);
  animation: ${isClosing ? popOut : popIn} 220ms ease-out forwards;
`;

export const deleteModalHeader = css`
  padding: 32px 24px 24px;
  background-color: ${theme.colors.white};
`;

export const deleteModalTitle = css`
  margin: 0;
  color: ${theme.colors.neutral[700]};
`;

export const deleteModalActions = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 74px;
  padding: 12px 16px;
  border-top: 1px solid ${theme.colors.neutral[200]};
  background-color: ${theme.colors.white};
`;

export const deleteModalCancel = css`
  && {
    padding: 11px 0 12px;
    color: ${theme.colors.primary.medium};
  }
`;
